import { Employee } from '../types/employee.types';
import { WEEKDAYS } from './working-info-schema';

/**
 * The work from home switch HR flips on an employee straight from the directory (legacy
 * `employee-list`). It lives on the `UserProfileDetail` and is separate from the edit tabs,
 * so saving a tab never touches it. Only someone holding `HRM_PERMISSIONS.PEOPLE.CHANGE_WFH`
 * sees the column; everybody works from home until it's switched off.
 *
 * Lunch and overtime used to be switches here too (and, in the legacy app, limited by region). They
 * are Blocks IAM permissions on the employee now — `HRM_PERMISSIONS.PEOPLE.LUNCH_ENABLED` /
 * `OVERTIME_ENABLED`, granted through roles — so there is nothing to flip per employee any more.
 */
export type WorkStatusChanges = Partial<Pick<Employee, 'IsWFH' | 'WFODays'>>;

/** Work from home is on unless it was explicitly switched off — new and never-set profiles count as on. */
export const isWorkingFromHome = (employee: Pick<Employee, 'IsWFH'> | null | undefined) =>
  employee?.IsWFH !== false;

/** Office days in week order, dropping anything that is not a weekday name. */
export const sortOfficeDays = (days?: readonly string[] | null) =>
  WEEKDAYS.filter((day) => (days ?? []).includes(day));

/** Switching work from home on: keeps whatever office days were already on file. */
export const workFromHomeOn = (employee: Employee): WorkStatusChanges => ({
  IsWFH: true,
  WFODays: sortOfficeDays(employee.WFODays),
});

/**
 * Switching work from home off. HR picks the office days first (at least one, otherwise
 * `undefined` so the caller keeps asking).
 */
export const workFromHomeOff = (officeDays?: readonly string[]): WorkStatusChanges | undefined => {
  const days = sortOfficeDays(officeDays);
  return days.length > 0 ? { IsWFH: false, WFODays: days } : undefined;
};
